import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Barbell, ArrowRight, CheckCircle, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Two-mode auth page sharing the same shell as Login:
 *   • mode="forgot" — collect an email, POST /auth/forgot-password,
 *                     show a "check your inbox" confirmation regardless of result.
 *   • mode="reset"  — token is in ?token=… ; collect a new password and POST
 *                     /auth/reset-password.
 */
export default function ForgotPassword({ mode }) {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const isReset = mode === "reset";

  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (isReset && !token) toast.error("Missing reset token in the URL");
  }, [isReset, token]);

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      // We still want to surface the friendly "check your inbox" message even
      // when the API rejects validation, to avoid email enumeration.
      setSent(true);
      console.warn("forgot-password error", err);
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Visual side — identical to Login */}
      <div className="relative hidden md:block">
        <img
          src="https://images.pexels.com/photos/8899512/pexels-photo-8899512.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
          alt="Mobility training"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-moss/80 via-moss/40 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/15 backdrop-blur grid place-items-center">
              <Barbell size={22} weight="bold" />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold">HealthSpan Training</div>
              <div className="text-xs uppercase tracking-[0.25em] text-white/70">Coach · Client Platform</div>
            </div>
          </div>
          <div className="max-w-md space-y-3">
            <div className="text-xs uppercase tracking-[0.3em] text-white/80">Reset your password</div>
            <h2 className="font-heading text-3xl leading-tight">
              We'll get you back to the floor in a minute.
            </h2>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-md space-y-6">
          {!isReset && !sent && (
            <form onSubmit={submitForgot} className="space-y-5" data-testid="forgot-password-form">
              <div>
                <div className="hs-label mb-3">Forgot password</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink">No worries.</h1>
                <p className="text-ink-2 mt-2">
                  Enter the email on your account and we'll send you a reset link. The link expires in 30 minutes.
                </p>
              </div>
              <div>
                <Label htmlFor="forgot-email" className="hs-label">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 h-12 rounded-xl"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full h-12 rounded-xl bg-terracotta hover:bg-terracotta-hover text-white font-medium hover:text-white"
                data-testid="forgot-submit-button"
              >
                {busy ? "Sending…" : (<span className="inline-flex items-center gap-2">Send reset link <ArrowRight size={16} /></span>)}
              </Button>
              <p className="text-xs text-ink-3">
                Remembered it? <Link to="/login" className="text-moss font-medium hover:underline" data-testid="back-to-login">Back to sign-in</Link>
              </p>
            </form>
          )}

          {!isReset && sent && (
            <div className="space-y-5" data-testid="forgot-sent-confirm">
              <div className="h-12 w-12 rounded-full bg-sage grid place-items-center text-moss">
                <EnvelopeSimple size={22} weight="fill" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-semibold text-ink">Check your inbox.</h1>
                <p className="text-ink-2 mt-2">
                  If an account exists for <b>{email}</b>, we just sent a reset link there.
                  It expires in <b>30 minutes</b>.
                </p>
                <p className="text-sm text-ink-3 mt-3">
                  Didn't get it? Check spam — or ask your coach to reset it for you from the People page.
                </p>
              </div>
              <Link to="/login">
                <Button variant="secondary" className="rounded-xl" data-testid="forgot-done-back">
                  Back to sign-in
                </Button>
              </Link>
            </div>
          )}

          {isReset && !done && (
            <form onSubmit={submitReset} className="space-y-5" data-testid="reset-password-form">
              <div>
                <div className="hs-label mb-3">Choose a new password</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink">Almost there.</h1>
                <p className="text-ink-2 mt-2">Pick something at least 8 characters long.</p>
              </div>
              <div>
                <Label htmlFor="new-password" className="hs-label">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-2 h-12 rounded-xl"
                  data-testid="reset-new-password"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password" className="hs-label">Confirm</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-2 h-12 rounded-xl"
                  data-testid="reset-confirm-password"
                />
              </div>
              <Button
                type="submit"
                disabled={busy || !token}
                className="w-full h-12 rounded-xl bg-terracotta hover:bg-terracotta-hover text-white font-medium hover:text-white"
                data-testid="reset-submit-button"
              >
                {busy ? "Saving…" : "Set new password"}
              </Button>
            </form>
          )}

          {isReset && done && (
            <div className="space-y-5" data-testid="reset-done">
              <div className="h-12 w-12 rounded-full bg-sage grid place-items-center text-moss">
                <CheckCircle size={26} weight="fill" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-semibold text-ink">Password updated.</h1>
                <p className="text-ink-2 mt-2">You can now sign in with your new password.</p>
              </div>
              <Button
                onClick={() => nav("/login")}
                className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white"
                data-testid="reset-go-login"
              >
                Go to sign-in <ArrowRight size={16} className="ml-1" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
